import React, { useEffect, useMemo, useState } from 'react';
import { BeheerLayout } from '../../components/beheer/BeheerLayout';
import { FilterSelect } from '../../components/beheer/FilterSelect';
import { LogTimeline } from '../../components/beheer/logboek/LogTimeline';
import { WhyChain } from '../../components/beheer/logboek/WhyChain';
import { agents } from '../../data/agents';
import { actionLevels } from '../../data/levels';
import { logHistory } from '../../data/beheer/logHistory';
import { useDb } from '../../hooks/useDb';
import { buildLogbook, filterLogbook } from '../../utils/logbook';
import type { LogFilters } from '../../types/beheer';
const EMPTY: LogFilters = {
  agent: 'alle',
  level: 'alle',
  outcome: 'alle',
  where: 'alle'
};
const AGENT_OPTIONS: {
  value: LogFilters['agent'];
  label: string;
}[] = [{
  value: 'alle',
  label: 'Alle'
}, ...agents.map(a => ({
  value: a.id,
  label: a.name
})), {
  value: 'systeem',
  label: 'Systeem'
}];
const LEVEL_OPTIONS: {
  value: LogFilters['level'];
  label: string;
}[] = [{
  value: 'alle',
  label: 'Alle'
}, ...actionLevels.map(l => ({
  value: l.id,
  label: l.label
}))];
const OUTCOME_OPTIONS: {
  value: LogFilters['outcome'];
  label: string;
}[] = [{
  value: 'alle',
  label: 'Alle'
}, ...(['uitgevoerd', 'gemeld', 'goedgekeurd', 'afgewezen', 'geweigerd', 'gestopt'] as const).map(o => ({
  value: o,
  label: o[0].toUpperCase() + o.slice(1)
}))];
const WHERE_OPTIONS: {
  value: LogFilters['where'];
  label: string;
}[] = [{
  value: 'alle',
  label: 'Alle'
}, {
  value: 'lokaal',
  label: 'Lokaal'
}, {
  value: 'cloud',
  label: 'Cloud'
}];
export function Logboek() {
  const live = useDb(t => t.log);
  const traces = useDb(t => t.traces);
  const [filters, setFilters] = useState<LogFilters>(EMPTY);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const all = useMemo(() => buildLogbook(logHistory, live, traces), [live, traces]);
  const shown = useMemo(() => filterLogbook(all, filters), [all, filters]);
  const selected = shown.find(e => e.entry.id === selectedId) ?? shown[0] ?? null;
  useEffect(() => {
    if (selected && selected.entry.id !== selectedId) setSelectedId(selected.entry.id);
  }, [selected, selectedId]);
  const set = <K extends keyof LogFilters,>(key: K) => (value: LogFilters[K]) => setFilters(f => ({
    ...f,
    [key]: value
  }));
  const filtered = JSON.stringify(filters) !== JSON.stringify(EMPTY);
  const cloudCount = all.filter(e => e.entry.where === 'cloud').length;
  const refused = all.filter(e => e.entry.outcome === 'geweigerd').length;
  return <BeheerLayout title="Logboek" intro="Alles wat Anna deed of weigerde, met de volledige redenering erachter." aside={<dl className="flex gap-8 text-[13px]" data-id="element-1892" data-mp="361h7hbbzroug">
          <div data-id="element-1893" data-mp="36064veua8n4x">
            <dt className="text-ink-muted" data-id="element-1894" data-mp="3tsd3kknhd67i">Rijen</dt>
            <dd className="mt-0.5 font-display text-[28px] font-light tabular-nums text-ink" data-id="element-1895" data-mp="3tqs9ew52vpa6">{all.length}</dd>
          </div>
          <div data-id="element-1896" data-mp="2fa5yvbaqtcd">
            <dt className="text-ink-muted" data-id="element-1897" data-mp="1n3kxvsgm1225">Via cloud</dt>
            <dd className="mt-0.5 font-display text-[28px] font-light tabular-nums text-ink" data-id="element-1898" data-mp="1n2025zik7d2p">{cloudCount}</dd>
          </div>
          <div data-id="element-1899" data-mp="1i2x3cwt6a336">
            <dt className="text-ink-muted" data-id="element-1900" data-mp="1wte6qlpdb32p">Geweigerd</dt>
            <dd className="mt-0.5 font-display text-[28px] font-light tabular-nums text-ink" data-id="element-1901" data-mp="1wsumuzg5q4m1">{refused}</dd>
          </div>
        </dl>} data-id="element-1891" data-mp="313floin6lbfa">
      <div className="mt-8 flex flex-wrap items-center gap-2" role="group" aria-label="Filters" data-id="element-1902" data-mp="144go2hh8v49f">
        <FilterSelect label="Agent" value={filters.agent} options={AGENT_OPTIONS} onChange={set('agent')} data-id="element-1903" data-mp="z6f6y23dpf0l" />
        <FilterSelect label="Niveau" value={filters.level} options={LEVEL_OPTIONS} onChange={set('level')} data-id="element-1904" data-mp="1d5x0eurmdgga" />
        <FilterSelect label="Uitkomst" value={filters.outcome} options={OUTCOME_OPTIONS} onChange={set('outcome')} data-id="element-1905" data-mp="1930ccojb8s4y" />
        <FilterSelect label="Verwerking" value={filters.where} options={WHERE_OPTIONS} onChange={set('where')} data-id="element-1906" data-mp="2ojiqqj97to3e" />
        {filtered && <button type="button" onClick={() => setFilters(EMPTY)} className="h-9 whitespace-nowrap rounded-full px-3 text-[13px] text-ink-muted transition-colors duration-150 hover:text-ink" data-id="element-1907" data-mp="2klkvayouq5rk">
            Wis filters
          </button>}
        <span className="ml-auto text-[13px] tabular-nums text-ink-muted" aria-live="polite" data-id="element-1908" data-mp="3ow63egehc53t">
          {shown.length} van {all.length}
        </span>
      </div>

      <div className="mt-6 grid gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)]" data-id="element-1909" data-mp="2m3k85f1xbnc">
        <div className="min-w-0" data-id="element-1910" data-mp="6nf1nnnruqkg">
          <LogTimeline events={shown} selectedId={selected?.entry.id ?? null} onSelect={setSelectedId} data-id="element-1911" data-mp="6mnkhhux8vdp" />
        </div>
        <div className="min-w-0" data-id="element-1912" data-mp="bhjcii05riqt">
          <div className="no-scrollbar lg:sticky lg:top-24 lg:max-h-[calc(100vh-7rem)] lg:overflow-y-auto" data-id="element-1913" data-mp="gejful5mi7e8">
            {selected ? <WhyChain key={selected.entry.id} event={selected} data-id="element-1914" data-mp="lf7i62zayanr" /> : <div className="rounded-3xl border border-dashed border-white/10 p-10 text-center text-[14px] text-ink-muted" data-id="element-1915" data-mp="qd918nwqa9z8">
                Kies een rij om te zien waarom Anna dit deed.
              </div>}
          </div>
        </div>
      </div>
    </BeheerLayout>;
}